import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MusicNotes, ArrowRight, Sparkle, Warning, Moon, Sun, Heart, Crown, Drop, BookOpen, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";

const PRIORITY_TINT = {
  high: { bg: "from-rose-50 to-orange-50", border: "border-rose-300", badge: "bg-rose-200 text-rose-900", label: "AFFLICTED · HIGH PRIORITY" },
  amplify: { bg: "from-emerald-50 to-teal-50", border: "border-emerald-300", badge: "bg-emerald-200 text-emerald-900", label: "STRONG · AMPLIFY" },
  regular: { bg: "from-amber-50 to-yellow-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-900", label: "MAINTENANCE" },
};

const TONE_ICONS = {
  calming: Moon,
  grounding: Moon,
  softening: Heart,
  solar: Sun,
  "courage-building": Crown,
  "lunar-healing": Moon,
  "fire-cooling": Drop,
  protective: Crown,
  "metabolism-shifting": Sparkle,
  "intellectual-quickening": BookOpen,
  "shukra-regenerating": Heart,
  "long-haul-easing": Clock,
  "guru-restoring": BookOpen,
  "rahu-anchoring": Sparkle,
  "ketu-deepening": Sparkle,
};

export default function AdminRagaRemediesPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  usePrefillBirthDetails(setForm, setPlaceQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_super_admin) { navigate("/"); }
  }, [user, authLoading, navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/admin/raga-remedies/compute", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='raga-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compute raga remedies");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !user?.is_super_admin) {
    return <PageShell><Header /><div className="p-10 text-center text-muted">Loading…</div><Footer /></PageShell>;
  }

  return (
    <PageShell>
      <Header />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <MusicNotes size={14} weight="duotone" /> Super-admin · internal tool
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Magic Remedies of Raga
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-2xl">
            Classical Hindustani raga prescriptions tailored to the seeker's natal placements — per-planet ragas, special chart combinations (Ketu in 2H, Mars in Leo, Saturn in Scorpio, etc.), and life-area healing recommendations.
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="raga-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="raga-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="raga-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(data || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="raga-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`raga-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="raga-submit">
            {loading ? "Composing your raga prescription…" : <>Generate raga remedies <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="raga-result">
            {/* === SPECIAL COMBINATIONS (the rule-book matches) === */}
            <section className="space-y-4">
              <div className="label-eyebrow text-muted">Special combinations · ragas matched to your specific placements</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="special-block">
                {data.special_combinations.map((sc, i) => {
                  const Icon = TONE_ICONS[sc.tone] || MusicNotes;
                  return (
                    <div key={i} className="rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200" data-testid={`special-${i}`}>
                      <div className="flex items-center gap-2 label-eyebrow text-indigo-900">
                        <Icon size={16} weight="duotone" /> {sc.tone}
                      </div>
                      <h4 className="font-serif-display text-xl mt-3 font-light leading-snug text-[#3F2D1B]">{sc.headline}</h4>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {sc.ragas.map((r, j) => (
                          <span key={j} className="text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-indigo-200 text-indigo-900">
                            {r}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm mt-3 font-light leading-snug text-[#3F2D1B]">{sc.why}</p>
                      <div className="text-[11px] text-muted mt-2 italic"><Clock size={11} weight="duotone" className="inline mr-1" /> {sc.when}</div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* === LIFE-AREA AFFLICTIONS === */}
            {data.life_area_recommendations && data.life_area_recommendations.length > 0 && (
              <section className="space-y-4">
                <div className="label-eyebrow text-rose-900 flex items-center gap-2">
                  <Warning size={14} weight="duotone" /> Life-area afflictions detected
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.life_area_recommendations.map((la, i) => (
                    <div key={i} className="rounded-2xl p-5 bg-rose-50 border border-rose-200" data-testid={`area-${i}`}>
                      <div className="text-[10px] font-bold tracking-wider text-rose-900">{la.trigger.toUpperCase()} · {la.severity.toUpperCase()}</div>
                      <h4 className="font-serif-display text-lg mt-2 font-light leading-snug text-[#3F2D1B]">{la.area}</h4>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {la.ragas.map((r, j) => (
                          <span key={j} className="text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-rose-200 text-rose-900">{r}</span>
                        ))}
                      </div>
                      <p className="text-xs mt-3 italic text-muted leading-snug">↳ {la.reason}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === PER-PLANET RAGAS === */}
            <section className="space-y-4">
              <div className="label-eyebrow text-muted">Per-planet ragas · daily maintenance + amplification</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="planets-block">
                {data.planet_recommendations.map((pr, i) => {
                  const t = PRIORITY_TINT[pr.priority] || PRIORITY_TINT.regular;
                  return (
                    <div key={i} className={`rounded-2xl p-5 bg-gradient-to-br ${t.bg} border ${t.border}`} data-testid={`planet-rec-${pr.planet}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${t.badge}`}>{t.label}</span>
                      </div>
                      <h4 className="font-serif-display text-xl mt-3 font-light text-[#3F2D1B]">{pr.planet}</h4>
                      <div className="text-xs text-muted mt-1">H{pr.house} · {pr.rashi} · {pr.dignity}</div>
                      <div className="mt-3 space-y-1.5">
                        {pr.ragas.map((r, j) => (
                          <div key={j} className="text-xs px-2.5 py-1.5 rounded-md bg-white/70 border border-soft">
                            <MusicNotes size={11} weight="duotone" className="inline mr-1" /> {r}
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] mt-3 italic text-muted leading-snug">{pr.why}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* === FOOTER NOTE === */}
            <section className="rounded-2xl p-6 bg-[#FAF4E8] border border-soft">
              <div className="label-eyebrow text-muted">How to use this prescription</div>
              <ul className="mt-3 text-sm font-light leading-snug text-[#3F2D1B] space-y-2">
                <li>· Start with the <b>special combinations</b> section — those are the most chart-specific matches.</li>
                <li>· For any afflicted planet (red badge), listen to its ragas for at least <b>21 consecutive days</b> at the same time of day for the karmic loop to start lifting.</li>
                <li>· Use headphones / good speakers — raga therapy works on micro-frequencies that compressed audio sometimes loses.</li>
                <li>· Source: ITC Sangeet Research Academy, Pt. Bhimsen Joshi, Ustad Vilayat Khan, Pt. Hariprasad Chaurasia archives on Spotify / YouTube.</li>
              </ul>
            </section>
          </div>
        )}
      </div>
      <Footer />
    </PageShell>
  );
}
